import { useEffect, useRef, useState } from "react";

// Shared "auto-scroll a message list" behavior for both the Buddies.js
// embedded chat panel and ChatPage.js:
// - jumps to the bottom the first time a chat's messages load
// - auto-scrolls to new messages only when the user is already near the
//   bottom (so scrolling up to read history is never fought)
// - otherwise surfaces `hasNewBelow` so the caller can show a small
//   "New messages" button instead of forcibly snapping the view down
export function useAutoScroll(messages, chatKey) {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const previousChatKeyRef = useRef(null);
  const previousLengthRef = useRef(0);
  const [hasNewBelow, setHasNewBelow] = useState(false);

  const isNearBottom = () => {
    const el = containerRef.current;
    if (!el) return true;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom < 120;
  };

  const scrollToBottom = (behavior = "auto") => {
    bottomRef.current?.scrollIntoView({ behavior, block: "end" });
    setHasNewBelow(false);
  };

  useEffect(() => {
    const chatChanged = previousChatKeyRef.current !== chatKey;
    const grew = messages.length > previousLengthRef.current;

    if (chatChanged) {
      // Opening a (new) chat always jumps straight to the newest message.
      previousChatKeyRef.current = chatKey;
      previousLengthRef.current = messages.length;
      // Wait a tick for the DOM to paint the messages before measuring.
      requestAnimationFrame(() => scrollToBottom("auto"));
      return;
    }

    if (grew) {
      if (isNearBottom()) {
        requestAnimationFrame(() => scrollToBottom("smooth"));
      } else {
        setHasNewBelow(true);
      }
    }

    previousLengthRef.current = messages.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, chatKey]);

  return { containerRef, bottomRef, hasNewBelow, scrollToBottom };
}
